import React, { useState } from 'react'
import { RefreshCw, Smartphone, Wifi, X } from 'lucide-react'
import { useCallStore, type ConnectionQuality } from '@/stores/callStore'
import { useConfirm } from '@/components/ui/ConfirmDialog'

/**
 * AC-34: live connection quality chip shown during an active call.
 * Good = mint, fair = neutral glass, poor = blush with pulse animation.
 * On 'poor', clicking reveals a panel with actionable fixes.
 * Respects prefers-reduced-motion for pulse.
 */
export function ConnectionQualityIndicator(): React.ReactElement | null {
  const quality = useCallStore((s) => s.connectionQuality)
  const [showPanel, setShowPanel] = useState(false)
  const { confirm } = useConfirm()

  if (!quality) return null

  const cfg = qualityConfig(quality)

  const handleReload = async () => {
    const ok = await confirm({
      title: 'Reload the app?',
      impact: 'This ends the call you’re on. Reloading can fix a poor connection.',
      confirmLabel: 'End call and reload',
      cancelLabel: 'Stay on the call',
    })
    if (ok) window.location.reload()
  }

  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        onClick={() => quality === 'poor' && setShowPanel((v) => !v)}
        aria-label={`Connection quality: ${quality}`}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          height: 22,
          padding: '0 8px',
          borderRadius: 'var(--r-pill)',
          border: `0.5px solid ${cfg.borderColor}`,
          background: cfg.bgColor,
          cursor: quality === 'poor' ? 'pointer' : 'default',
          touchAction: 'manipulation',
        }}
      >
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: 'var(--r-pill)',
            background: cfg.dotColor,
            display: 'inline-block',
            ...(quality === 'poor' ? {
              animation: 'pulse 1.2s ease-in-out infinite',
            } : {}),
          }}
          // prefers-reduced-motion handled by browser on animation
        />
        <span
          style={{
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '0.10em',
            textTransform: 'uppercase',
            color: cfg.textColor,
          }}
        >
          {quality}
        </span>
      </button>

      {showPanel && quality === 'poor' && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: '100%',
            marginTop: 8,
            width: 256,
            borderRadius: 'var(--r-md)',
            border: '0.5px solid rgba(255,255,255,0.08)',
            background: 'rgba(255,255,255,0.04)',
            backdropFilter: 'blur(32px) saturate(180%)',
            WebkitBackdropFilter: 'blur(32px) saturate(180%)',
            boxShadow: '0 10px 30px -8px rgba(0,0,0,0.6)',
            padding: '12px',
            zIndex: 50,
          }}
          className="animate-fade-up"
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <p style={{ fontSize: 12, fontWeight: 600, color: '#FFFFFF' }}>Connection issues</p>
            <button
              type="button"
              onClick={() => setShowPanel(false)}
              aria-label="Close"
              style={{
                width: 20,
                height: 20,
                borderRadius: 'var(--r-pill)',
                border: '0.5px solid rgba(255,255,255,0.08)',
                background: 'rgba(255,255,255,0.04)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: 'var(--midnight-text3)',
              }}
            >
              <X style={{ width: 10, height: 10 }} />
            </button>
          </div>
          <p style={{ fontSize: 12, color: 'var(--midnight-text3)', marginBottom: 10, lineHeight: 1.5 }}>
            Packet loss, jitter or latency is degraded. Try one of:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <ActionRow
              icon={<Wifi style={{ width: 12, height: 12 }} />}
              label="Switch network"
              hint="Hotspot or nearby Wi-Fi may be more stable"
            />
            <button
              type="button"
              onClick={handleReload}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                padding: '8px 10px',
                borderRadius: 'var(--r-sm)',
                border: '0.5px solid rgba(255,255,255,0.06)',
                background: 'rgba(255,255,255,0.03)',
                cursor: 'pointer',
                textAlign: 'left',
                touchAction: 'manipulation',
                width: '100%',
              }}
              onPointerDown={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
              onPointerUp={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.03)')}
              onPointerLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.03)')}
            >
              <RefreshCw style={{ width: 12, height: 12, color: 'var(--midnight-text3)', marginTop: 2, flexShrink: 0 }} />
              <div>
                <p style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.80)', marginBottom: 2 }}>
                  Reload browser
                </p>
                <p style={{ fontSize: 12, color: 'var(--midnight-text3)', lineHeight: 1.4 }}>
                  Ends this call and rebuilds the WebRTC session
                </p>
              </div>
            </button>
            <ActionRow
              icon={<Smartphone style={{ width: 12, height: 12 }} />}
              label="Use mobile fallback"
              hint="Dial the caller directly from your mobile phone"
            />
          </div>
        </div>
      )}
    </div>
  )
}

function ActionRow({
  icon,
  label,
  hint,
}: {
  icon: React.ReactNode
  label: string
  hint: string
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        padding: '8px 10px',
        borderRadius: 'var(--r-sm)',
        border: '0.5px solid rgba(255,255,255,0.06)',
        background: 'rgba(255,255,255,0.03)',
      }}
    >
      <div style={{ color: 'var(--midnight-text3)', marginTop: 2, flexShrink: 0 }}>{icon}</div>
      <div>
        <p style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.80)', marginBottom: 2 }}>{label}</p>
        <p style={{ fontSize: 12, color: 'var(--midnight-text3)', lineHeight: 1.4 }}>{hint}</p>
      </div>
    </div>
  )
}

interface QualityConfig {
  dotColor: string
  bgColor: string
  borderColor: string
  textColor: string
}

function qualityConfig(q: ConnectionQuality): QualityConfig {
  switch (q) {
    case 'good':
      return {
        dotColor: 'var(--midnight-mint)',
        bgColor: 'rgba(168,230,206,0.10)',
        borderColor: 'rgba(168,230,206,0.25)',
        textColor: 'var(--midnight-mint)',
      }
    case 'fair':
      return {
        dotColor: 'rgba(255,255,255,0.60)',
        bgColor: 'rgba(255,255,255,0.06)',
        borderColor: 'rgba(255,255,255,0.12)',
        textColor: 'rgba(255,255,255,0.70)',
      }
    case 'poor':
      return {
        dotColor: 'var(--midnight-blush)',
        bgColor: 'rgba(244,181,189,0.10)',
        borderColor: 'rgba(244,181,189,0.25)',
        textColor: 'var(--midnight-blush)',
      }
  }
}
